<template>
    <div class="trial-form" v-if="!pageLoading">
        <div class="trial-form__left-side elisa" v-if="isElisa">
            <div class="trial-form__inner">
                <h1 class="trial-form__header" v-html="freeTrial.header"></h1>
                <img src="@/assets/images/elisa-logo.svg" class="trial-form__image" alt=""/>
                <ul class="trial-form__list" v-for="i in 2">
                    <li v-html="$t(`FreeTrial.list_point_${i}_elisa`)"></li>
                </ul>
            </div>
        </div>
        <div class="trial-form__left-side" v-else>
            <div class="trial-form__inner">
                <h1 class="trial-form__header" v-html="freeTrial.header"></h1>
                <img src="@/assets/images/trial-form.png" class="trial-form__image" alt=""/>
                <ul class="trial-form__list" v-for="i in 4">
                    <li v-html="$t(`FreeTrial.list_point_${i}`)"></li>
                </ul>
            </div>
        </div>
        <div class="trial-form__right-side" :class="{'with-button': isLoggedIn}">
            <div class="trial-form__inner">
                <div class="trial-form__error" v-if="error !== null">
                    {{ error }}
                </div>
                <template v-if="isTrialForm && isLoggedIn && !signed">
                    <TotemButton @click="signIn" :disabled="sending" color="secondary" class="trial-form__join-button" :label="$t('AgreementForm.join_here')" />
                </template>
                <template v-if="isTrialForm && isLoggedIn && !signed">
                    <p class="trial-form__thank-you center" v-html="$t('FreeSubscriptions.thank_you_info_after_join_in_trial')"></p>
                </template>
                <template v-if="isTrialForm && !isLoggedIn && !signed">
                    <h1 class="trial-form__header second" v-html="$t('FreeTrial.header_elisa')" v-if="isElisa"></h1>
                    <h1 class="trial-form__header second" v-html="$t('FreeTrial.header')" v-else></h1>
                    <span class="trial-form__description" v-html="descriptionText"></span>

                    <div class="trial-form__inputs">
                        <div class="trial-form__input">
                            <TotemInput v-model="firstName" :hasError="$v.firstName.$error" @blur.native="$v.firstName.$touch()" :label="$t('General.first_name')" isRequired fullWidth/>
                            <totem-form-finnish-label-error :field="$v.firstName" :label="$t('General.first_name')"/>
                        </div>

                        <div class="trial-form__input">
                            <TotemInput v-model="lastName" :hasError="$v.lastName.$error" @blur.native="$v.lastName.$touch()" :label="$t('General.last_name')" isRequired fullWidth/>
                            <totem-form-finnish-label-error :field="$v.lastName" :label="$t('General.last_name')"/>
                        </div>

                        <div class="trial-form__input">
                            <TotemInput v-model="userEmail" :hasError="$v.userEmail.$error" @blur.native="$v.userEmail.$touch()" :label="$t('General.email_address')" isRequired fullWidth/>
                            <totem-form-finnish-label-error :field="$v.userEmail" :label="$t('General.email_address')"/>
                        </div>

                        <div class="trial-form__input">
                            <TotemSelect showSelectedOption :hasError = "$v.jobTitle.$error" v-model="$v.jobTitle.$model" :options="sortedJobTitles" :label="$t('General.title')" isRequired width="100%" />
                            <totem-form-finnish-label-error :field="$v.jobTitle" :label="$t('General.title')"/>
                        </div>

                        <div class="trial-form__input">
                            <TotemPasswordInput :hasError="$v.password.$error" v-model="password" @blur.native="$v.password.$touch()" :label="$t('General.password')" isRequired fullWidth/>
                            <totem-form-finnish-label-error :field="$v.password" :label="$t('General.password')"/>
                        </div>

                        <div class="trial-form__input">
                            <TotemPasswordInput :hasError="$v.passwordRepeat.$error" v-model="passwordRepeat" @blur.native="$v.passwordRepeat.$touch()" :label="$t('General.repeat_password')" isRequired fullWidth/>
                            <totem-form-finnish-label-error :field="$v.passwordRepeat"/>
                        </div>
                    </div>

                    <div class="trial-form__terms">
                        <TotemCheckbox class="trial-form__terms-checkbox" v-model="$v.terms.$model" name="terms" />
                        <TotemInputLabel isRequired class="trial-form__terms-label" v-html="$t('General.terms_and_policy_info_updated')">
                        </TotemInputLabel>
                    </div>
                    <totem-form-finnish-label-error :field="$v.terms" v-if="!$v.terms.$model" :label="$t('General.terms_and_policy')"/>

                    <TotemButton
                        class="trial-form__submit-button"
                        disabled
                        color="basic"
                        icon="spinner"
                        fullWidth
                        v-if="sending"
                    />
                    <TotemButton
                        @click="sendForm"
                        class="trial-form__submit-button"
                        :disabled="sending || isInSummitTimeRange"
                        color="primary"
                        fullWidth
                        :label="freeTrial.button"
                        v-else
                    />
                </template>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex'
    import { validationMixin } from 'vuelidate'
    import sortAlphabeticallyAscByJobTitle from 'components/shared/mixins/sorting/sortAlphabeticallyAscByJobTitle'
    import router from '../router'
    import summitEventHelper from 'helpers/summitEventHelper'
    const { validName } = require('components/shared/global-forms/custom-validations.js')
    const { required, minLength, email, sameAs } = require('vuelidate/lib/validators')

    export default {
        name: 'trial-form',
        mixins: [ validationMixin, sortAlphabeticallyAscByJobTitle, summitEventHelper ],
        computed: {
            ...mapGetters({
                isLoggedIn: 'isLoggedIn',
                languageCountryCode: 'Languages/getSelectedCountryCode'
            }),
            freeTrial () {
                if (!this.freeSubscriptionForm) {
                    return
                }
                if (this.isFormRegistration) {
                    return {
                        header: this.$t('FormHeader.registration'),
                        button: this.$t('FormButton.registration')
                    }
                }

                if (this.isElisa) {
                    return {
                        header: this.$t('FreeTrial.trial_header_elisa'),
                        button: this.$t('FreeTrial.trial_button')
                    }
                }

                return {
                    header: this.$t('FreeTrial.trial_header'),
                    button: this.$t('FreeTrial.trial_button')
                }
            },
            isFormRegistration () {
                return this.freeSubscriptionForm && this.freeSubscriptionForm.type === 'registration'
            },
            descriptionText () {
                return this.freeSubscriptionForm.descriptionTranslation !== '' ? this.$t(this.freeSubscriptionForm.descriptionTranslation) : this.freeSubscriptionForm.formDescription
            },
            isElisa () {
                return this.$route.params.slug === 'elisa'
            }
        },
        validations: {
            firstName: {
                required,
                validName
            },
            lastName: {
                required,
                validName
            },
            userEmail: {
                required,
                email
            },
            password: {
                required,
                minLength: minLength(8)
            },
            passwordRepeat: {
                repeatPassword: sameAs('password')
            },
            terms: {
                checkboxRequired: sameAs(() => true)
            },
            jobTitle: {
                required
            }
        },
        data () {
            return {
                firstName: null,
                lastName: null,
                userEmail: null,
                password: null,
                jobTitle: null,
                passwordRepeat: null,
                triedSubmit: false,
                error: null,
                terms: false,
                jobTitles: [],
                sortedJobTitles: [],
                isTrialForm: false,
                signed: false,
                sending: false,
                freeSubscriptionForm: {},
                pageLoading: true
            }
        },
        watch: {
            isLoggedIn () {
                this.error = null
                this.onSendFormAndLogin()
            },
            isInSummitTimeRange: {
                handler: 'showTrialDisabledModal',
                immediate: true
            }
        },
        methods: {
            showTrialDisabledModal () {
                if (this.isInSummitTimeRange) {
                    this.$modal.show('trial-period-disabled')
                } else {
                    this.$modal.hide('trial-period-disabled')
                }
            },
            onSendFormAndLogin () {
                if (this.isLoggedIn && this.signed && !this.error) {
                    router.push({ 'name': 'home-courses' })
                }
            },
            async refreshData () {
                try {
                    this.freeSubscriptionForm = await this.$store.dispatch('User/loadFreeSubscription', this.$route.params.slug)
                    this.isTrialForm = true

                    this.jobTitles = await this.$store.dispatch('User/loadJobTitles')
                    this.sortedJobTitles = this.sortAlphabeticallyAsc(this.jobTitles)
                } catch (exc) {
                    this.handleError(exc.code || exc.status)
                }
            },
            handleError (codeOrStatus, responseMessage) {
                this.sending = false
                if (codeOrStatus === 621) {
                    this.error = this.$t('General.already_have_account')
                } else if (codeOrStatus === 2001) {
                    this.error = this.$t('FreeSubscriptions.Error.CannotYetJoin')
                } else if (codeOrStatus === 2002) {
                    this.error = this.$t('FreeSubscriptions.Error.YouAlreadyJoined')
                } else if (codeOrStatus === 2003) {
                    this.error = this.$t('FreeSubscriptions.Error.FormExpired')
                } else if (codeOrStatus === 404) {
                    this.$router.push({ name: 'page-currently-unavailable' })
                } else if (codeOrStatus === 'ERR_BAD_REQUEST') {
                    this.error = this.$t(responseMessage)
                } else {
                    this.$router.push({ name: 'page-currently-unavailable' })
                }
            },
            async signIn () {
                try {
                    this.error = null
                    this.alreadyExists = false
                    this.sending = true
                    await this.$store.dispatch('User/sendFreeSubscriptionRequestForExisting', {
                        slug: this.$route.params.slug,
                    })
                    this.signed = true
                } catch (err) {
                    this.handleError(err.code || err.status)
                }
            },
            async sendForm (e) {
                if (this.isInSummitTimeRange) {
                    return false
                }
                this.error = null
                this.$v.$touch()
                if (this.$v.$invalid) {
                    e.preventDefault()
                } else {
                    this.sending = true
                    let formData = {
                        firstName: this.firstName,
                        lastName: this.lastName,
                        userEmail: this.userEmail,
                        password: this.password,
                        title: this.title,
                        jobTitle: null,
                        preferredLanguage: this.languageCountryCode
                    }

                    if (this.jobTitle && this.jobTitle !== 0) {
                        formData.jobTitle = this.jobTitle.id
                    }
                    try {
                        await this.$store.dispatch('User/sendFreeSubscription', {
                            slug: this.$route.params.slug,
                            freeSubscription: formData
                        })
                        this.signed = true
                        if (!this.isLoggedIn) {
                            this.$router.push({ name: 'free-subscriptions-thank-you', params: { slug: this.$route.params.slug } })
                        }
                    } catch (exc) {
                        const responseError = exc.response ? exc.response.data.error : null
                        this.handleError(exc.code || exc.status, responseError.message)
                    }
                }
            }
        },
        async created () {
            const taskName = 'loadingFreeSubscriptionsPage'
            try {
                await this.$store.dispatch('LoaderScreen/addTaskToLoader', { key: taskName, message: 'Loading Free subscriptions page' })
                await this.refreshData()
                this.pageLoading = false
            } catch (exc) {}
            this.$store.dispatch('LoaderScreen/removeTaskFromLoader', taskName)
        }
    }
</script>

<style lang="scss" scoped>
    .trial-form {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: stretch;
        margin: 0 auto;
        padding-bottom: 65px;

        &__join-button {
            font-size: 30px !important;
            padding: 10px 20px;
            height: auto !important;
            margin: auto;
        }

        &.app-content {
            [class*="desktop"] & {
                flex-direction: row;
                padding: 145px 0 65px;
            }
        }

        &__left-side {
            display: flex;
            padding: 30px;
            justify-content: center;
            background: $white;

            [class*="desktop"] & {
                border-radius: 24px 0 0 24px;
                width: 360px;
            }

            &.elisa {
                background: #0012b4;
            }
        }

        &__right-side {
            display: flex;
            flex-direction: row;
            justify-content: center;
            padding: 30px 30px 30px 40px;
            background: $white;

            &.with-button {
                justify-content: center !important;
            }

            [class*="desktop"] & {
                justify-content: flex-start;
                width: 740px;
                border-radius: 0 24px 24px 0;
            }
        }

        &__inner {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
        }

        &__image {
            margin: 0 0 30px;
            align-self: center;
        }

        &__header {
            font-family: PPAgrandir-Medium, sans-serif;
            color: $lavender;
            margin: 0 0 20px;
            text-align: center;
            font-size: 3.6rem;

            &.first {
                text-align: center;
                align-self: center;
            }

            &.second {
                font-family: PlusJakartaSans-Regular, sans-serif;
                color: $black;
                font-size: 2.2rem;

                [class*="mobile"] & {
                    font-size: 2.4rem;
                }
            }
        }

        &__list {
            font-family: PlusJakartaSans-Medium, sans-serif;
            font-size: 1.6rem;
            text-align: left;
            position: relative;

            & > li {
                margin: 0 0 20px 25px;

                &:before {
                    font-family: 'icomoon';
                    content: "\e925";
                    color: $lavender;
                    margin: 0 10px 0 0;
                    position: absolute;
                    left: 0;
                }
            }

        }

        &__description {
            display: block;
            padding: 0 0 50px 0;
            text-align: left;
            line-height: 150%;
            font-size: 1.6rem;
        }

        &__inputs {
            display: flex;
            flex-wrap: wrap;
            gap: 18px;
            padding: 0 0 15px;
        }

        &__input {
            width: 100%;
            display: flex;
            flex-direction: column;
            align-items: flex-start;

            [class*="desktop"] & {
                width: calc(50% - 10px);
            }

            .totem-input {
                border-color: $silver_chalice;
            }
        }

        &__terms {
            display: flex;
        }

        &__terms-label {
            text-align: left;
            margin: 0 !important;
        }

        &__terms-label::v-deep a {
            color: $lavender;
            cursor: pointer;
            text-decoration: underline;
        }

        &__terms-checkbox {
            padding: 5px
        }

        &__link {
            font-family:  Nunito-ExtraBold, sans-serif;
            color: $rust_orange;
            cursor: pointer;
            text-decoration: underline;
        }

        &__submit-button.totem-button {
            margin: 20px auto 0;
            line-height: 30px;
        }

        &__thank-you {
            max-width: 400px;
            font-size: 3rem;
            margin: auto;

            &.center {
                text-align: center;
            }
        }

        &__error {
            margin: 20px 0;
            color: $guardsman_red;
        }
    }
</style>
